/**
 * frameTrack — map a continuous clock time to a frame index.
 *
 * The frame shown at time `t` is the most recent frame at or before `t` (the
 * `<video>` step-function: "the frame for the current position"). Implemented as
 * a binary search with a forward cursor, so steady playback is O(1) amortized and
 * a random seek is O(log n).
 */

import { Frame } from './types';

/** Return frames sorted ascending by timestamp (stable; does not mutate input). */
export function sortFrames<T extends Frame>(frames: T[]): T[] {
  return [...frames].sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Index of the greatest frame with `timestamp <= t`, clamped to [0, n-1].
 * Before the first frame returns 0; after the last returns n-1; empty returns -1.
 *
 * `cursorHint` (the previously returned index) makes forward playback O(1): we
 * check the hint and its neighbour before falling back to binary search.
 */
export function frameIndexAt(
  frames: Frame[],
  t: number,
  cursorHint = 0
): number {
  const n = frames.length;
  if (n === 0) return -1;
  if (t < frames[0].timestamp) return 0;
  if (t >= frames[n - 1].timestamp) return n - 1;

  // Fast path: the common case is "same frame" or "advanced by one".
  const h = cursorHint >= 0 && cursorHint < n ? cursorHint : 0;
  if (frames[h].timestamp <= t && (h + 1 >= n || frames[h + 1].timestamp > t)) {
    return h;
  }
  if (
    h + 1 < n &&
    frames[h + 1].timestamp <= t &&
    (h + 2 >= n || frames[h + 2].timestamp > t)
  ) {
    return h + 1;
  }

  // Binary search for the greatest index with timestamp <= t.
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (frames[mid].timestamp <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}
